// @ts-strict-ignore
import { useUser } from "@dashboard/auth";
import useAppChannel from "@dashboard/components/AppLayout/AppChannelContext";
import { hasPermissions } from "@dashboard/components/RequirePermissions";
import {
  PermissionEnum,
  useHomeActivitiesQuery,
  useHomeAnaliticsQuery,
  useHomeNotificationsQuery,
  useHomeOrdersTodayQuery,
  useHomeOutOfStockProductsLazyQuery,
  useHomeTopProductsQuery,
} from "@dashboard/graphql";
import useNavigator from "@dashboard/hooks/useNavigator";
import createDialogActionHandlers from "@dashboard/utils/handlers/dialogActionHandlers";
import { mapEdgesToItems } from "@dashboard/utils/maps";
import React from "react";

import { getUserName } from "../../misc";
import HomePage from "../components/HomePage";
import { homeUrl, HomeUrlDialog, HomeUrlQueryParams } from "../urls";
import { deriveSalesToday, getUtcToday } from "../utils";

/**
 * One page of orders is enough to total a normal trading day. Beyond it the
 * figure falls back to the ordersTotal aggregate rather than under-reporting.
 */
const ORDERS_TODAY_PAGE_SIZE = 100;

/**
 * The dialog lists every product fetched and takes its remainder from
 * totalCount; 20 rows fill it without turning it into the product list.
 */
const OUT_OF_STOCK_PAGE_SIZE = 20;

interface HomeSectionProps {
  params: HomeUrlQueryParams;
}

const HomeSection = ({ params }: HomeSectionProps) => {
  const navigate = useNavigator();
  const { user } = useUser();
  const { channel } = useAppChannel();

  // The open dialog is a URL param, like every other dashboard dialog, so
  // browser back from an order or product page lands on the open dialog.
  const [openDialog, closeDialog] = createDialogActionHandlers<
    HomeUrlDialog,
    HomeUrlQueryParams
  >(navigate, homeUrl, params);

  const noChannel = !channel && typeof channel !== "undefined";

  const userPermissions = user?.userPermissions || [];
  const hasPermissionToManageOrders = hasPermissions(userPermissions, [
    PermissionEnum.MANAGE_ORDERS,
  ]);
  const hasPermissionToManageProducts = hasPermissions(userPermissions, [
    PermissionEnum.MANAGE_PRODUCTS,
  ]);

  const today = getUtcToday();

  const {
    data: homeActivities,
    loading: homeActivitiesLoading,
    error: homeActivitiesError,
  } = useHomeActivitiesQuery({
    skip: noChannel,
    variables: {
      hasPermissionToManageOrders,
    },
  });

  const {
    data: homeTopProducts,
    loading: homeTopProductsLoading,
    error: homeTopProductsError,
  } = useHomeTopProductsQuery({
    skip: noChannel,
    variables: {
      channel: channel?.slug,
      hasPermissionToManageProducts,
    },
  });

  const {
    data: homeNotificationsData,
    loading: homeNotificationsLoaing,
    error: homeNotificationsError,
  } = useHomeNotificationsQuery({
    skip: noChannel,
    variables: { channel: channel?.slug },
  });

  const {
    data: homeAnaliticsData,
    loading: homeAnaliticsLoading,
    error: homeAnaliticsError,
  } = useHomeAnaliticsQuery({
    skip: noChannel,
    variables: {
      channel: channel?.slug,
      hasPermissionToManageOrders,
    },
  });

  const {
    data: ordersTodayData,
    loading: ordersTodayLoading,
    error: ordersTodayError,
  } = useHomeOrdersTodayQuery({
    skip: noChannel || !hasPermissionToManageOrders,
    variables: {
      channel: channel?.slug,
      today,
      first: ORDERS_TODAY_PAGE_SIZE,
    },
  });

  // Lazy: the out-of-stock count comes from homeNotifications, so the list is
  // only worth fetching once the dialog is open.
  const [
    fetchOutOfStockProducts,
    {
      data: outOfStockData,
      loading: outOfStockLoading,
      error: outOfStockError,
      called: outOfStockCalled,
    },
  ] = useHomeOutOfStockProductsLazyQuery({
    variables: {
      channel: channel?.slug,
      first: OUT_OF_STOCK_PAGE_SIZE,
    },
  });

  // Keyed on the URL rather than on a click, so a page loaded with the
  // dialog already open — a reload, or back from a product — fetches too.
  React.useEffect(() => {
    if (params.action === "out-of-stock" && !outOfStockCalled) {
      fetchOutOfStockProducts();
    }
  }, [params.action, outOfStockCalled]);

  const salesToday = deriveSalesToday(ordersTodayData, channel?.currencyCode);

  // Rare enough to be worth knowing about if it ever fires — in an effect, so
  // it reports the transition rather than repeating on every render.
  React.useEffect(() => {
    if (salesToday.isPartialDay) {
      console.warn(
        `[home] more than ${ORDERS_TODAY_PAGE_SIZE} orders today; ` +
          "falling back to the ordersTotal aggregate for the sales figure",
      );
    }
  }, [salesToday.isPartialDay]);

  return (
    <HomePage
      activities={{
        data: mapEdgesToItems(homeActivities?.activities)?.reverse(),
        loading: homeActivitiesLoading,
        hasError: !!homeActivitiesError,
      }}
      topProducts={{
        data: mapEdgesToItems(homeTopProducts?.productTopToday),
        loading: homeTopProductsLoading,
        hasError: !!homeTopProductsError,
      }}
      notifications={{
        data: {
          productsOutOfStock:
            homeNotificationsData?.productsOutOfStock.totalCount,
        },
        loading: homeNotificationsLoaing,
        hasError: !!homeNotificationsError,
      }}
      analitics={{
        data: {
          sales: salesToday.isPartialDay
            ? homeAnaliticsData?.salesToday?.gross
            : salesToday.collected,
          orders: salesToday.contributing,
          awaitingPaymentCount: salesToday.awaitingPaymentCount,
          isPartialDay: salesToday.isPartialDay,
        },
        loading: ordersTodayLoading || homeAnaliticsLoading,
        hasError: !!ordersTodayError || !!homeAnaliticsError,
      }}
      outOfStockDetail={{
        data: {
          products: mapEdgesToItems(outOfStockData?.productsOutOfStock) ?? [],
          totalCount: outOfStockData?.productsOutOfStock?.totalCount ?? 0,
        },
        loading: outOfStockLoading,
        hasError: !!outOfStockError,
      }}
      openDialog={params.action}
      onOpenDialog={openDialog}
      onCloseDialog={closeDialog}
      today={today}
      userName={getUserName(user, true)}
      noChannel={noChannel}
    />
  );
};

export default HomeSection;
